<template>
  <div
    v-if="currentShoppingCart"
    class="shopCartToggle absolute w-full text-paragraph pt-12"
  >
    <OnClickOutside
      class="shoppingcart flyout flex flex-col w-full lg:top-full left-0 sm:left-auto sm:right-0 absolute sm:w-[400px] bg-white z-1 text-paragraph shadow-lg shadow-gray-700"
      @trigger="close"
    >
      <template v-if="showReservationOnly">
        <ShoppingCartContent
          :cart="reservationCart"
          :cart-type="CartType.RESERVATION_CART"
        />
      </template>
      <template v-if="showShoppingOnly">
        <ShoppingCartContent
          :cart="currentShoppingCart"
          :cart-type="CartType.SHOPPING_CART"
        />
      </template>
      <template v-if="showBothCarts">
        <div>
          <button
            id="shoppingCartSwitch"
            class="cart-button"
            :class="{ active: toggleCartView }"
            @click="switchCartLayout(true)"
          >
            Warenkorb
            <span class="cart-amount-number" :class="{ active: toggleCartView }">{{
              currentShoppingCart?.itemList?.length
            }}</span>
          </button>
          <button
            id="reservationCartSwitch"
            class="cart-button"
            :class="{ active: !toggleCartView }"
            @click="switchCartLayout(false)"
          >
            Reservierung
            <span class="cart-amount-number" :class="{ active: !toggleCartView }">{{
              reservationCart?.itemList?.length
            }}</span>
          </button>
        </div>
        <template v-if="toggleCartView">
          <ShoppingCartContent
            :cart="currentShoppingCart"
            :cart-type="CartType.SHOPPING_CART"
          />
        </template>
        <template v-if="!toggleCartView">
          <ShoppingCartContent
            :cart="reservationCart"
            :cart-type="CartType.RESERVATION_CART"
          />
        </template>
      </template>
      <template v-if="showEmptyCart">
        <div class="py-8 text-center bg-white">Es liegen keine Artikel im Warenkorb.</div>
        <NuxtLink
          :to="'/shop/checkout?step=1'"
          :external="true"
          class="btn-primary text-lg py-4 text-center block"
        >
          <BasicIcon
            class="w-4 mr-2 text-primary-contrast"
            :name="'cart-shopping-solid'"
          />Zum Warenkorb
        </NuxtLink>
      </template>
    </OnClickOutside>
  </div>
  <div
    v-if="currentShoppingCart == null"
    class="shopCartToggle absolute w-full text-paragraph top-full"
  >
    <OnClickOutside
      class="shoppingcart flyout flex flex-col border border-solid border-secondary w-full lg:top-full left-0 sm:left-auto sm:right-0 absolute sm:w-[400px]"
      @trigger="close"
    >
      <div class="py-8 text-center bg-white">
        Es liegen keine Artikel im Warenkorb.
      </div>
      <NuxtLink
        :to="'/shop/checkout?step=1'"
        :external="true"
        class="btn-primary text-lg py-4 text-center block"
      >
        <BasicIcon class="w-4 mr-2 text-primary-contrast" :name="'cart-shopping-solid'" />
        Zum Warenkorb
      </NuxtLink>
    </OnClickOutside>
  </div>
</template>
<script setup lang="ts">
import { CartType } from "@expert/types";
import { OnClickOutside } from "@vueuse/components";
import ShoppingCartContent from "~/components/Content/ShoppingCartContent.vue";
import { useCurrentShoppingCart, useCurrentReservationCart } from "~/composables/states";

const currentShoppingCart = useCurrentShoppingCart();
const reservationCart = useCurrentReservationCart();

const reservationCountRef = useReservationCartItemsCount();
const shoppingCartCountRef = useShoppingCartItemsCount();

const reservationCount = computed(() => reservationCountRef.value || 0);
const shoppingCartCount = computed(() => shoppingCartCountRef.value || 0);

const hasReservationCart = computed(() => reservationCart.value !== undefined);
const hasShoppingCart = computed(() => currentShoppingCart.value !== undefined);

const showReservationOnly = computed(
  () => hasReservationCart.value && shoppingCartCount.value <= 0 && reservationCount.value > 0,
);
const showShoppingOnly = computed(
  () => hasShoppingCart.value && shoppingCartCount.value > 0 && reservationCount.value <= 0,
);
const showBothCarts = computed(
  () =>
    hasReservationCart.value &&
    hasShoppingCart.value &&
    shoppingCartCount.value > 0 &&
    reservationCount.value > 0,
);
const showEmptyCart = computed(
  () => shoppingCartCount.value <= 0 && reservationCount.value <= 0,
);

const toggleCartView = ref(true);

function switchCartLayout(switcher: boolean) {
  if (switcher) {
    toggleCartView.value = true;
  } else {
    toggleCartView.value = false;
  }
}

const emit = defineEmits(["changeFlyOutState"]);
function close() {
  emit("changeFlyOutState", false);
}
</script>

<style lang="postcss" scoped>
#shoppingCartSwitch {
  @apply border-r;
}
.cart-button {
  @apply w-1/2 border-b mt-1 py-2 px-2.5;
  &.active {
    @apply text-primary font-bold;
  }
}
.cart-amount-number {
  @apply bg-paragraph-light py-0 px-1 rounded-full text-white;
  &.active {
    @apply bg-primary font-bold;
  }
}
</style>
